// 关键词和语义理解分析相关Hooks

import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  cancelTopicAnalysisTask,
  checkSummaryConfig,
  createKeywordAnalysisTask,
  getKeywordAnalysisTaskCallbackStatus,
  getKeywordAnalysisTaskInfo,
  getTopicSelectList,
  readAnalysisTaskReport,
  updateKeywordAnalysisTask,
} from '@op/services';
import { useCallback, useEffect, useRef, useState } from 'react';
import { notification } from 'tea-component';
import { TGptErrorType } from '../pages/KeywordCardAnalysis/GPTSummaryContent';
import { useStateRef } from '@op/hooks/common';
import { GPTStatus } from '@op/constants/GPT';
import { EStepStatus, ITaskStep } from '../component/TaskAnalysis';
import useLanguage from '@op/hooks/useLanguage';
import {
  deepCompareSteps,
  transformCallbacksToSteps,
} from './useTaskProgress/utils';
import { getLinkDomain } from '@op/utils/url';

/**
 * 创建关键词分析任务
 * @returns
 */
export function useCreateKeywordAnalysis(props?: {
  showError?: boolean;
  isCreator?: boolean;
}) {
  const { showError = true, isCreator = false } = props || {};
  const [loadingRef, loading, setLoading] = useStateRef(false);
  const { formatMessage } = useLanguage();
  /**
   * 创建分析任务
   * @param params
   * @returns {task_id, summary_collect_id}
   */
  function createAnalysisTask(params: any) {
    // 保证只有自己才能创建
    if (loadingRef.current || !isCreator) {
      return Promise.resolve({});
    }
    setLoading(true);
    return createKeywordAnalysisTask({ ...params, domain: getLinkDomain() })
      .then((data: any) => {
        return data;
      })
      .catch(() => {
        if (showError) {
          notification.error({
            description: formatMessage('Create failed'),
          });
        }

        return {};
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function updateAnalysisTask(params: any) {
    if (loadingRef.current) {
      return Promise.resolve({});
    }
    setLoading(true);
    return updateKeywordAnalysisTask(params)
      .then((data: any) => {
        return data;
      })
      .catch(() => {
        notification.error({
          description: formatMessage('Edit failed'),
        });
        return {};
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return { loading, createAnalysisTask, updateAnalysisTask };
}

/**
 * 暂停topic分析任务
 * @returns
 */
export function useStopTopicAnalysis() {
  const { formatMessage } = useLanguage();
  const [loadingRef, loading, setLoading] = useStateRef(false);
  /**
   * 暂停分析任务
   * @param params
   * @returns {task_id, summary_collect_id}
   */
  function updateAnalysis(params: any) {
    if (loadingRef.current) {
      return Promise.resolve({});
    }
    setLoading(true);
    return cancelTopicAnalysisTask(params)
      .then((data: any) => {
        notification.success({
          description: formatMessage('The report has been manually stopped.'),
        });
        return data;
      })
      .catch((error: any) => {
        notification.error({
          description:
            error?.message || formatMessage('Failed to stop analysis task'),
        });
        return {};
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return { loading, updateAnalysis, loadingRef };
}

/**
 * 这里的任务状态一开始是准备给停止使用,现在的停止由外部控制,所以这些状态现在作用有限
 * init inqueue callback_success api_send_success retry ： In queue
 * running： Generating
 * finish success nodata：Done
 * failed api_send_fail callback_failed crawling_failed：Summary failed
 */
export enum ETaskStatus {
  INIT = 'In queue', // 初始化
  PROCESSING = 'Generating', // 处理中
  COMPLETED = 'Done', // 已完成
  FAILED = 'Summary failed', // 失败
  STOP = 'Stopped', // 手动停止
  AI_TAGGING = 'AI Tagging', // AI 标注中
  TAGGING_FAILED = 'Tagging failed', // AI 标注失败
  NO_NEW_DATA = 'NO New Data',
}

export const IsSuccessStatus = [
  ETaskStatus.STOP,
  ETaskStatus.FAILED,
  ETaskStatus.COMPLETED,
  ETaskStatus.TAGGING_FAILED,
];

/**
 * 获取关键词分析详情
 * keyword: completed 结束
 * @returns
 */
export function useKeywordAnalysisDetail(props?: {
  timer?: number;
  needRead?: boolean;
}) {
  const { timer: propsTimer = 3000, needRead = false } = props || {};
  const [isTopicAnalysis, setIsTopicAnalysis] = useState(false);
  const [showChildrenRef, showChildren, setShowChildren] = useStateRef(false);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<any>(null);
  const [summaryId, setSummaryId] = useState<string | null>(null);
  const [summaryStatus, setSummaryStatus] = useState<TGptErrorType>();
  const [taskStatus, setTaskStatus] = useState<ETaskStatus>(ETaskStatus.INIT);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [topicFilter, setTopicFilter] = useState<any>(null);
  const [updateGptParams, setUpdateGptParams] = useState<any>(null);

  const destroy = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const { wrapperCancelRequest, handleStop } = useCancelRequestHook({
    destroy,
    outControl: true,
  });

  const getSummaryIsSuccess = useCallback((data: any) => {
    const { task_id, summary_status, task_status } = data || {};
    return (
      task_id ||
      summary_status !== 0 ||
      task_status === 'complete_without_summary'
    );
  }, []);

  const initData = useCallback(() => {
    setDetail(null);
    setSummaryId(null);
    setTaskStatus(ETaskStatus.INIT);
    setTopicFilter(null);
    setSummaryStatus(undefined);
  }, []);

  const requestAnalysisDetail = useCallback(
    (
      params: any,
      options: {
        callbackStop?: (data: any) => boolean;
        callbackShowChildren?: (data: any) => boolean;
        showChildrenCallback?: () => void;
        timer?: number;
      },
    ) => {
      const {
        callbackStop,
        callbackShowChildren,
        timer = propsTimer,
        showChildrenCallback,
      } = options;

      setLoading(true);

      function repeatRequest() {
        destroy();
        timerRef.current = setTimeout(() => {
          requestAnalysisDetail(params, options);
        }, timer);
      }

      /**
       * 获取GPT修改的参数
       * 新【topic / keyword 】无version： 取last_keyword_analysis_task_queue_id
       * 新【topic / keyword 】有version： 取version_id & is_version = true
       * 旧keyword: id
       * @param data
       * @returns
       */
      function getUpdateParams(data: any) {
        const {
          last_keyword_analysis_task_queue_id,
          version_id,
          id,
          data_type,
        } = data || {};
        if (!data_type) return null;

        if (data_type !== 'new') {
          return { id, data_type };
        }

        if (version_id) {
          return { id: version_id, is_version: true, data_type };
        }

        return { id: last_keyword_analysis_task_queue_id, data_type };
      }

      function setData(status: ETaskStatus, data: any) {
        const {
          task_instance_id,
          task_id,
          last_keyword_analysis_task_queue_id,
          summary_status,
          summary_error_message,
          keyword_analysis_task_category,
          keyword_analysis_task_type,
        } = data || {};
        setTaskStatus(status);
        setDetail(data);
        setTopicFilter((value: any) => {
          if (task_instance_id !== value?.task_instance_id) {
            return { task_instance_id };
          }
          return value;
        });

        setSummaryId(task_id);
        setSummaryStatus({
          code: summary_status,
          message: summary_error_message,
        });
        setIsTopicAnalysis(keyword_analysis_task_category === 'topic');

        setUpdateGptParams(getUpdateParams(data));

        if (needRead && keyword_analysis_task_type === 'recurring') {
          readThisReport({ id: last_keyword_analysis_task_queue_id });
        }
      }

      wrapperCancelRequest(getKeywordAnalysisTaskInfo, params)
        .then(data => {
          const { task_status: status } = data || {};
          const { text = status } = GPTStatus?.[status] || {};
          const task_status = text as ETaskStatus;
          // 直接结束的状态
          const isStopStatus = IsSuccessStatus.includes(task_status);
          // 判断是否需要停止请求
          const isStop = callbackStop ? callbackStop(data) : isStopStatus;

          // 没有显示过子组件，并且要显示子组件了
          if (!showChildrenRef.current && callbackShowChildren?.(data)) {
            setData(task_status, data);
            setShowChildren(true);
            showChildrenCallback?.();

            if (isStop) {
              setLoading(false);
              return;
            }
          }

          // 指定停止状态
          if (isStop) {
            setData(task_status, data);
            setLoading(false);
            return;
          }

          // 其他情况继续请求
          repeatRequest();
        })
        .catch(error => {
          console.log(error);
        });
    },
    [],
  );

  function readThisReport(params: any) {
    readAnalysisTaskReport(params).catch(() => {});
  }

  return {
    loading,
    requestAnalysisDetail,
    detail,
    summaryId,
    taskStatus,
    topicFilter,
    initData,
    summaryStatus,
    handleStop,
    isTopicAnalysis,
    showChildren,
    getSummaryIsSuccess,
    updateGptParams,
  };
}

/**
 * 获取话题分析回调状态
 * @param timer 轮询时间
 * @returns
 */
export function useTopicTaskCallbackStatus({
  timer = 3000,
  requestInfo,
  defaultExpanded,
}: {
  timer: number;
  requestInfo: () => Promise<void>;
  defaultExpanded: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [steps, setSteps] = useState<ITaskStep[]>([]);
  const [taskTime, setTaskTime] = useState<{
    start_time: string;
    end_time: string;
  }>();
  const [taskStatus, setTaskStatus] = useState();

  const destroy = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const { wrapperCancelRequest, handleStop } = useCancelRequestHook({
    destroy,
  });

  const requestTopicTaskStatus = useCallback((params: any) => {
    destroy();
    setIsExpanded(defaultExpanded);
    const repeatRequest = () => {
      destroy();
      timerRef.current = setTimeout(() => {
        requestTopicTaskStatus(params);
      }, timer);
    };

    wrapperCancelRequest(getKeywordAnalysisTaskCallbackStatus, params)
      .then(data => {
        const {
          callbacks,
          status: task_status,
          start_time,
          end_time,
        } = data || {};
        const newSteps = transformCallbacksToSteps(callbacks);
        setTaskStatus(task_status);
        setSteps(oldValue => {
          if (deepCompareSteps(oldValue, newSteps)) {
            return oldValue;
          }
          return newSteps;
        });
        setTaskTime({ start_time, end_time });
        if (task_status === EStepStatus.FAILED) {
          destroy();
          requestInfo();
          return;
        }
        if (task_status === EStepStatus.COMPLETED) {
          requestInfo();
          setIsExpanded(false);
          return;
        }

        repeatRequest();
      })
      .catch((error: any) => {
        console.log(error);
      });
  }, []);

  return {
    requestTopicTaskStatus,
    steps,
    handleStop,
    taskStatus,
    isExpanded,
    setIsExpanded,
    taskTime,
  };
}

export function useTopicList({
  defaultValue,
  filter,
  autoRequest = true,
}: {
  defaultValue: string[];
  filter: any;
  autoRequest?: boolean;
}) {
  const [topicList, setTopicList] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState(defaultValue);

  function requestTopicList() {
    if (filter?.task_id) {
      setLoading(true);
      return getTopicSelectList(filter)
        .then((data: any) => {
          const { topics = [] } = data || {};
          setTopicList(
            topics.map((item: any) => ({ text: item, value: item })),
          );
          setValue(topics);
          return topics;
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    }
  }

  useEffect(() => {
    if (autoRequest) {
      requestTopicList();
    }
  }, [filter]);

  return { topicList, loading, value, setValue, requestTopicList };
}

export function useSummaryConfig() {
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  function request() {
    checkSummaryConfig()
      .then((data: any) => {
        const { keyword_topic_analysis = {} } = data || {};
        setHasPermission(!!keyword_topic_analysis?.allow_unlimited_date_range);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    request();
  }, []);

  return { loading, hasPermission };
}
